import React, { useMemo, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { getUpiPersona } from '../utils/insights';
import { formatINR } from '../utils/leakBuckets';
import { useModalA11y } from '../utils/useModalA11y';

/**
 * Anonymous shareable UPI Reality Check card.
 */
export default function UpiShareCard({ open, onClose }) {
    const { allTransactions } = useAppContext();
    const cardRef = useRef(null);
    const [copied, setCopied] = useState(false);
    useModalA11y({ onClose, isOpen: open });

    const persona = useMemo(() => getUpiPersona(allTransactions), [allTransactions]);

    if (!open) return null;

    const copyFlex = async () => {
        try {
            await navigator.clipboard.writeText(persona.flexLine);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = persona.flexLine;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const shareNative = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'My UPI Reality Check',
                    text: persona.flexLine,
                    url: 'https://spenderly.in',
                });
            } catch { /* cancelled */ }
        } else {
            copyFlex();
        }
    };

    return (
        <div
            className="sp-modal-overlay"
            style={{ zIndex: 1300 }}
            role="presentation"
            onClick={onClose}
        >
            <div
                className="sp-modal share-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="share-persona-title"
                onClick={(e) => e.stopPropagation()}
            >
                <button type="button" className="sp-modal-close" onClick={onClose} aria-label="Close">×</button>
                <div className="upi-share-card" ref={cardRef}>
                    <div className="upi-share-brand">spenderly</div>
                    <p className="upi-share-eyebrow">My UPI Persona Last Month</p>
                    <h2 className="upi-share-persona" id="share-persona-title">{persona.persona}</h2>
                    {persona.tagline && (
                        <p className="upi-share-tagline">{persona.tagline}</p>
                    )}
                    <ul className="upi-share-stats">
                        {persona.topFoodApp && (
                            <li>
                                <span>Top Food App</span>
                                <strong>{persona.topFoodApp.name} ({persona.topFoodApp.orders} orders)</strong>
                            </li>
                        )}
                        <li>
                            <span>Micro-Taps (&lt;₹100)</span>
                            <strong>{persona.microTaps} times</strong>
                        </li>
                        <li>
                            <span>Estimated Spend</span>
                            <strong>{formatINR(persona.estimatedSpend)}</strong>
                        </li>
                        <li>
                            <span>Actual Spend</span>
                            <strong>{formatINR(persona.actualSpend)}</strong>
                        </li>
                    </ul>
                    <p className="upi-share-footer">Analyzed locally with Spenderly.in — No bank login needed.</p>
                </div>

                <div className="leak-actions sp-modal-actions-stack">
                    <button type="button" className="sp-btn-primary" onClick={shareNative}>
                        Share anonymously
                    </button>
                    <button type="button" className="sp-btn-ghost" onClick={copyFlex}>
                        {copied ? 'Copied' : 'Copy Chai vs Zepto line'}
                    </button>
                    <button type="button" className="sp-btn-ghost" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
